import * as React from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';

import { Container } from '../../../layout/Container';

import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../__types__/generated/GeneralValuation';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const KjopeEllerSelge: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [lead, { error }] = useMutation<
    GeneralValuation,
    GeneralValuationVariables
  >(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.valuation?.success) {
        window.scroll(0, 0);
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Eiendomssalg kan være forvirrende. Bør du kjøpe eller selge først? - PrivatMegleren"
        description="Det er mange faktorer som spiller inn ved et vellykket eiendomssalg - her er våre beste tips"
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/content/kjope_selge.jpeg"
        opacity={0.2}
        height="600px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Eiendomssalg kan være forvirrende. Bør du kjøpe eller selge først?
            Og når på året lønner det seg egentlig å selge eiendom?
          </Heading>
        </HeroContent>
      </Hero>
      <Container
        className="padding-top"
        style={{ ...pageFadeIn, paddingTop: '80px', paddingBottom: '80px' }}
      >
        <Paragraph center>
          <b>
            Det er mange faktorer som spiller inn ved et vellykket boligsalg -
            her er våre beste tips.
          </b>
        </Paragraph>
        <Paragraph center>
          Å selge eiendom er for de aller fleste forbundet med de største
          økonomiske transaksjonene man gjør i livet, og derfor settes hele
          følelsesregisteret i sving. Mange faktorer spiller inn på
          eiendomsmarkedet, men tilbud og etterspørsel er til syvende og sist
          den styrende indikasjonen på om budrunden tar fyr eller ikke. Er det
          mange eiendommer på markedet, kan kjøperne tillate seg å opptre mer
          lunkent. Er det derimot rift om eiendommene, vil prisene presses opp.
        </Paragraph>
        <Paragraph center>
          E-takst er en ny og velkommen standard som gir en god og dokumenterbar
          vurdering av eiendoms verdi. Før e-takst ble innført, gjorde megler en
          skjønnsmessig verdivurdering av eiendommer som skulle legges ut for
          salg. Prisvurdering av eiendom ble basert på udefinerte standarder, og
          meglerhusene opererte med ulike parametere. E-takst er et nyttig
          verktøy som stiller konkrete krav til dokumentasjon av meglers
          verdivurdering, slik at en prisvurdering alltid baseres på
          dokumenterbare analyser.Baseres på sammenligning med tilsvarende
          eiendom i samme område i nær fortid.
        </Paragraph>
        <Heading tag="h3" center>
          3 ting som sikrer et godt eiendomssalg
        </Heading>
        <Paragraph center>
          <b>1. Tenk taktisk når du velger tidspunkt for å selge eiendommen</b>
        </Paragraph>
        <Paragraph center>
          Det er ikke lenger slik at det lønner seg å selge eiendom på høsten.
          Nå finnes det ingen gylden regel for når på året det er best å selge,
          men det kan lønne seg å bruke litt sunn fornuft. Har du en enebolig i
          Bærum, legger du den for eksempel ikke ut for salg i vinterferien når
          potensielle kjøpere er bortreist. Har du derimot en hytte du vil
          selge, kan det lønne seg å legge visningshelgen til høstferien, når
          folk allikevel er i fjellet og det er stemning for vandring, bålpanner
          og hyttekos.
        </Paragraph>
        <Paragraph center>
          <b>2. Tenk visuelt når du presenterer eiendommen</b>
        </Paragraph>
        <Paragraph center>
          Det visuelle er nøkkelen for å vekke interessen hos kjøperne, så det
          vil alltid lønne seg å gjøre eiendommen så presentabel som mulig før
          salg, samt investere i profesjonell fotografering til prospektet.
          Mange kvier seg for å selge i mørketiden når terrassen er nedsnødd,
          men en god eiendomsmegler vet hvilke fotografer som kan forvandle et
          trist eksteriør med tomme blomsterkasser til et vakkert og
          stemningsfullt vintertablå. Be gjerne om arbeidsprøver fra tidligere
          eiendomssalg fotografen har jobbet med.
        </Paragraph>
        <Image src="https://cdn.reeltime.no/pm_assets/content/kjope_selge_2.jpeg" />
        <Heading tag="h3" center>
          <b>3. Tenk markedsføring for et effektivt eiendomssalg</b>
        </Heading>
        <Paragraph center>
          God, målrettet markedsføring er en avgjørende faktor i de fleste
          eiendomssalg. Kanskje koster det litt mer, men her har du alt å vinne.
          En dyktig eiendomsmegler er god til å fange opp interessen til den
          riktige målgruppen for nettopp din eiendom. Kanskje er det
          småbarnsfamilier, kanskje ungkarer eller unge par i etableringsfasen?
          Uansett, så er det viktig å få rikelig med oppmerksomhet fra den
          kundegruppen som er potensielle kjøpere. Treffer dere blink med
          markedsføringen i form av tekst, bilder og ikke minst på visning, vil
          salget mest sannsynlig skje mye raskere.
        </Paragraph>
        <Paragraph center>
          <b>Kjøpe eller selge først?</b>
        </Paragraph>
        <Paragraph center>
          Et eiendomssalg kan være en nervepirrende prosess, og mange kvier seg
          for å kjøpe nytt før salget er i boks. – Etter tjuefem år i bransjen
          har jeg fortsatt ikke opplevd kunder som har brent seg på å kjøpe
          først. Med dagens e-takst som er en dokumenterbar verdivurdering, har
          eiere et realistisk bilde av hva eiendommen deres vil selges for, sier
          Frode Furuhovde, kjedesjef salg/drift i PrivatMegleren. De aller
          fleste som selger eiendom i Norge er også eiendomskjøpere i samme
          prosess, og så lenge du kjøper og selger i samme markedsklima, spiller
          det ikke så stor rolle om du kjøper før du selger. – Et unntak er
          selvsagt hvis man bor ekstremt avsidesliggende. Da bør man selge
          først, sier Furuhovde.
        </Paragraph>
        <Paragraph center>
          <b>Andre avgjørende faktorer som påvirker eiendomssalget</b>
        </Paragraph>
        <Paragraph center>
          Selv om det er mye man kan gjøre selv for å optimalisere
          eiendomssalget, er det også andre faktorer som spiller inn som man
          ikke får gjort så mye med. De siste årene har det blitt bygd mange nye
          eiendommer, samtidig som befolkningsveksten er negativ i de fleste av
          landets storbyer. Denne kombinasjonen fører over tid til et overskudd
          av eiendommer på markedet, noe som igjen fører til et kjøpers marked.
          Samtidig så vil den positive stemningen i norsk økonomi kombinert med
          lav arbeidsledighet bidra til at folk fortsatt ønsker å satse i
          eiendomsmarkedet. – Det aller viktigste å tenke på når man skal selge
          eiendom er faktisk seg selv. Egne behov for når man skal selge og hvor
          man skal flytte vil alltid være knyttet til hvor man er i livet, og
          livet er som kjent uforutsigbart. Det er strengt tatt ikke noe feil
          tidspunkt å selge på, avslutter Furuhovde.
        </Paragraph>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Kontakt
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default KjopeEllerSelge;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;

const Image: React.FC<{ src: string }> = ({ src }) => {
  return (
    <StyledImageWrapper>
      <img src={src} alt="bilde-inspirasjon" />
    </StyledImageWrapper>
  );
};

const StyledImageWrapper = styled.div`
  display: block;
  width: 100%;
  margin: 30px 0;

  img {
    width: 100%;
  }
`;
